import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  collection,
  getFirestore,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import Spinner from "./Spinner";
import { useNavContext } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import Nav from "./Nav";
import Paginador from "./Paginador";
import ProductDetailsModal from "./ProductDetailsModal";
import EditProductModal from "./EditProductModal";

const ProductosRelacionados = () => {
  const { teamOwnerId } = useAuth();
  const { categoriaNombre } = useParams(); // Usamos el nombre de la categoría
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const { searchTerm, proximasAVencer, proximosEventos, setSearchTerm } =
    useNavContext();
  const [selectedProductos, setSelectedProductos] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(25); // Valor inicial
  const [currentPage, setCurrentPage] = useState(1);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleEditProduct = (producto) => {
    setProductoSeleccionado(producto);
    setIsEditModalOpen(true);
  };

  const handleMostrarProducto = (producto) => {
    setProductoSeleccionado(producto);
    setIsDetailsModalOpen(true);
  };

  useEffect(() => {
    if (!categoriaNombre || !teamOwnerId) return;

    setLoading(true);

    const db = getFirestore();
    const nombreCat = decodeURIComponent(categoriaNombre).trim().toUpperCase();

    // reference a ambas colecciones
    const colClientes = collection(
      db,
      "usuarios",
      teamOwnerId,
      "productosClientes"
    );
    const colProvee = collection(
      db,
      "usuarios",
      teamOwnerId,
      "productosProveedores"
    );

    // misma consulta para ambas
    const qClientes = query(colClientes, where("categoria", "==", nombreCat));
    const qProvee = query(colProvee, where("categoria", "==", nombreCat));

    // escuchamos las dos en paralelo y fusionamos resultados
    const unsub1 = onSnapshot(qClientes, (snap) => {
      setProductos((prev) => {
        const otros = prev.filter((p) => p.__source !== "cli");
        const cli = snap.docs.map((d) => ({
          id: d.id,
          __source: "cli",
          ...d.data(),
        }));
        return [...otros, ...cli];
      });
      setLoading(false);
    });

    const unsub2 = onSnapshot(qProvee, (snap) => {
      setProductos((prev) => {
        const otros = prev.filter((p) => p.__source !== "pro");
        const pro = snap.docs.map((d) => ({
          id: d.id,
          __source: "pro",
          ...d.data(),
        }));
        return [...otros, ...pro];
      });
      setLoading(false);
    });

    // limpieza
    return () => {
      unsub1();
      unsub2();
    };
  }, [categoriaNombre, teamOwnerId]);
  // Filtramos los productos según el término de búsqueda.
  // Si searchTerm es vacío, se devuelven todos los productos.
  const filteredProductos = React.useMemo(() => {
    if (!productos || productos.length === 0) return [];
    if (!searchTerm || searchTerm.trim() === "") return productos;
    const searchLower = searchTerm.toLowerCase();
    const filtrados = productos.filter((producto) => {
      const searchableFields = [
        producto.nombre?.toLowerCase() || "",
        producto.descripcion?.toLowerCase() || "",
      ];
      return searchableFields.some((field) => field.includes(searchLower));
    });
    console.log("Productos filtrados:", filtrados.length);
    return filtrados;
  }, [productos, searchTerm]);

  // Paginación: calculamos el subconjunto a mostrar
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProductos.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Selecciona todos los productos visibles en la página actual
      const allProductosIds = currentProducts.map((producto) => producto.id);
      setSelectedProductos(allProductosIds);
    } else {
      setSelectedProductos([]);
    }
  };

  const handleSelectProducto = (productoId) => {
    setSelectedProductos((prevSelected) => {
      if (prevSelected.includes(productoId)) {
        return prevSelected.filter((id) => id !== productoId); // Deseleccionar
      } else {
        return [...prevSelected, productoId]; // Seleccionar
      }
    });
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1); // Reiniciamos a la primera página
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDeleteSelected = async () => {
    try {
      const confirmed = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción eliminará los productos seleccionados de la categoría.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        scrollbarPadding: false,
      });

      if (!confirmed.isConfirmed) return;

      const db = getFirestore();
      const productosRef = collection(db, "usuarios", teamOwnerId, "productos"); // ✔️ ruta correcta
      const productosOut = [];

      for (const prodId of selectedProductos) {
        const prodDocRef = doc(productosRef, prodId);
        const prodSnap = await getDoc(prodDocRef);

        if (prodSnap.exists()) {
          // quitamos la categoría (o ponla en null / "", según tu lógica)
          await updateDoc(prodDocRef, { categoria: null });
          productosOut.push(prodId);
        }
      }

      // estado local
      setProductos((prev) => prev.filter((p) => !productosOut.includes(p.id)));
      setSelectedProductos([]);

      await Swal.fire({
        title: "¡Productos eliminados!",
        text: "Los productos seleccionados se eliminaron correctamente de la categoría.",
        icon: "success",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Ok",
        scrollbarPadding: false,
      });
    } catch (err) {
      console.error("Error al eliminar productos:", err);
      Swal.fire({
        title: "Error",
        text: "Hubo un problema al eliminar los productos. Intenta nuevamente.",
        icon: "error",
        confirmButtonColor: "#d33",
        scrollbarPadding: false,
      });
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
        handleSearch={searchTerm}
      />

      <div className="task-filters">
        <div className="header-actions-proyectos">
          <button className="action-button-proyectos">
            <i className="bi bi-funnel-fill"></i> Todos
            <i className="bi bi-chevron-down" style={{ fontSize: "12px" }}></i>
          </button>
          <div className="buscador-pedidos">
            <i className="bi bi-search"></i>
            <input
              type="text"
              className="buscador-input"
              placeholder="Buscar productos relacionados..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="task-filters-right">
          <div
            className="view-buttons-container"
            style={{ position: "relative" }}
          >
            <div style={{ marginLeft: "5px" }}>
              {selectedProductos.length > 0 && (
                <div className="delete-button-container">
                  <button
                    type="button"
                    className="delete-button"
                    onClick={handleDeleteSelected}
                  >
                    Eliminar
                  </button>
                </div>
              )}
            </div>
            <button className="view-button">
              <i className="bi bi-three-dots-vertical"></i>
            </button>
            <button className="exportar-button">
              <i
                className="bi bi-cloud-arrow-down"
                style={{ marginRight: "5px" }}
              ></i>
              Exportar
            </button>
          </div>
        </div>
      </div>

      <div className="cotizaciones-table">
        <div className="tabla-cotizaciones-wrapper">
          <div className="tabla-cotizaciones-content">
            {filteredProductos.length === 0 ? (
              searchTerm.trim() ? (
                /* ─── Sin resultados de búsqueda ─── */
                <div className="no-resultados">
                  <div
                    className="mensaje-sin-resultados"
                    style={{ marginTop: 30 }}
                  >
                    <p style={{ fontSize: 14 }}>
                      No hay resultados disponibles.
                    </p>
                  </div>
                  <div className="imagen-sin-resultados">
                    <img
                      src="/img/noSearch.svg"
                      alt="Sin resultados"
                      className="sin-resultados-imagen"
                      width={350}
                      height={350}
                    />
                  </div>
                </div>
              ) : (
                /* ─── No hay productos relacionados aún ─── */
                <div className="no-resultados">
                  <div
                    className="mensaje-sin-resultados"
                    style={{ marginTop: 30 }}
                  >
                    <p
                      style={{
                        textAlign: "center",
                        margin: "20px 0",
                        color: "#888",
                      }}
                    >
                      No hay productos relacionados aún.
                    </p>
                  </div>
                  <div className="imagen-sin-resultados">
                    <img
                      src="/img/no-data.svg"
                      alt="Sin datos"
                      width={350}
                      height={350}
                    />
                  </div>
                </div>
              )
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        className="checkbox-select-all check"
                      />
                    </th>
                    <th>Nombre</th>
                    <th>Número de Parte</th>
                    <th>Costo</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentProducts.map((producto) => (
                    <tr key={producto.id}>
                      <td className="check">
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={selectedProductos.includes(producto.id)}
                          onChange={() => handleSelectProducto(producto.id)}
                        />
                      </td>
                      <td style={{ width: "40%" }}>{producto.nombre}</td>
                      <td>{producto.numeroDeParteProveedor}</td>
                      <td>
                        {typeof producto.costo === "number"
                          ? producto.costo.toFixed(2)
                          : typeof producto.costo === "string" &&
                            !isNaN(Number(producto.costo))
                          ? Number(producto.costo).toFixed(2)
                          : "N/A"}
                      </td>
                      <td className="acciones-container">
                        <div className="acciones-icons">
                          <div
                            className="icon-wrapper"
                            onClick={() => handleMostrarProducto(producto)}
                          >
                            <i className="bi bi-eye"></i>
                            <span className="tooltipCategorias">
                              Mostrar Producto
                            </span>
                          </div>
                          <div
                            className="icon-wrapper"
                            onClick={() => handleEditProduct(producto)}
                          >
                            <i className="bi bi-pencil"></i>
                            <span className="tooltipCategorias">Editar</span>
                          </div>
                          <div className="icon-wrapper">
                            <i className="bi bi-shield-slash"></i>
                            <span className="tooltipCategorias">
                              Desactivar
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {filteredProductos.length > 0 && (
            <div className="tabla-cotizaciones-footer">
              <div className="paginador-container">
                <Paginador
                  totalItems={filteredProductos.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <ProductDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        categoria={productoSeleccionado}
      />

      <EditProductModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        product={productoSeleccionado}
      />
    </>
  );
};

export default ProductosRelacionados;
